// components/editor/WorkflowEditor.tsx

import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ReactFlow, {
    Background,
    BackgroundVariant,
    Controls,
    MiniMap
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
    Layout,
    Spin,
    Button,
    Form,
    Input,
    Modal,
    List,
    Space,
    Dropdown,
    Menu,
    Typography,
    Card,
    Table,
    Tag,
    Empty,
    App
} from 'antd';
import {
    ArrowLeftOutlined,
    PlusOutlined,
    SaveOutlined,
    UploadOutlined,
    DownloadOutlined,
    ExportOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    MenuOutlined,
    ArrowRightOutlined,
    BranchesOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useWorkflowEditor } from '../../hooks/useWorkflowEditor';
import { NodeEditor } from './NodeEditor';
import {nodeTypes} from "@/modules/action-flow/config";

const { Sider, Content } = Layout;
const { TextArea } = Input;
const { Title, Text } = Typography;

const WorkflowEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const {
        // State
        nodes,
        edges,
        selectedNode,
        maxLevel,
        isAddNodeModalOpen,
        newNodeType,
        isLoadModalOpen,
        pastedConfig,
        currentTransitions,
        fileInputRef,
        workflowName,
        workflowDescription,
        workflowVersion,
        isLoading,

        // Setters
        setSelectedNode,
        setIsAddNodeModalOpen,
        setNewNodeType,
        setIsLoadModalOpen,
        setPastedConfig,
        setWorkflowName,
        setWorkflowDescription,
        setWorkflowVersion,

        // Node operations
        onNodesChangeHandler,
        onEdgesChange,
        onConnect,
        onNodeClick,
        updateNode,
        addNode,
        removeNode,
        moveNode,

        // File operations
        handleFileUpload,
        handlePasteConfig,
        loadConfigFromJson,

        // Save operation
        saveWorkflow,
    } = useWorkflowEditor(id);

    // Export configuration as JSON
    const exportConfiguration = () => {
        const config = {
            workflowId: id || `workflow-${Date.now()}`,
            name: workflowName,
            description: workflowDescription,
            version: workflowVersion,
            states: nodes,
        };

        const processedConfig = JSON.stringify(config, null, 2);

        // Create and trigger download
        const blob = new Blob([processedConfig], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${workflowName.replace(/\s+/g, '_')}_v${workflowVersion}.json`;
        document.body.appendChild(link);
        link.click();

        // Clean up
        setTimeout(() => {
            URL.revokeObjectURL(url);
            document.body.removeChild(link);
        }, 100);
    };

    // Node type options for dropdown
    const nodeTypeOptions = useMemo(() => [
        { key: 'maker', label: 'Initiator' },
        { key: 'validator', label: 'Validator' },
        { key: 'router', label: 'Router' },
        { key: 'executor', label: 'Executor' },
        { key: 'timer', label: 'Timer' },
        { key: 'alerter', label: 'Alerter' },
        { key: 'approved', label: 'Approved' },
        { key: 'rejected', label: 'Rejected' },
    ], []);

    const { modal } = App.useApp();

    // Save confirmation dialog
    const showSaveConfirmation = () => {
        modal.confirm({
            title: 'Save Workflow Configuration',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure you want to save this workflow configuration?',
            onOk: saveWorkflow,
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Layout className="h-screen">
            {/* Left sidebar - Configuration panel */}
            <Sider width={400} className="bg-gray-100 p-4 overflow-y-auto">
                <div className="p-4 bg-white flex items-center mb-4 rounded-md shadow-sm">
                    <Link to="/admin/action-flow/configurations" className="mr-4">
                        <Button icon={<ArrowLeftOutlined />}>
                            Back to Configurations
                        </Button>
                    </Link>
                </div>

                <Card className="mb-4">
                    <Title level={4}>Workflow Configuration</Title>
                    <Form layout="vertical">
                        <Form.Item label="Name">
                            <Input
                                value={workflowName}
                                onChange={(e) => setWorkflowName(e.target.value)}
                                placeholder="Enter workflow name"
                            />
                        </Form.Item>
                        <Form.Item label="Description">
                            <TextArea
                                value={workflowDescription}
                                onChange={(e) => setWorkflowDescription(e.target.value)}
                                rows={3}
                                placeholder="Enter workflow description"
                            />
                        </Form.Item>
                        <Form.Item label="Version">
                            <Input
                                value={workflowVersion}
                                onChange={(e) => setWorkflowVersion(e.target.value)}
                                placeholder="1.0"
                            />
                        </Form.Item>
                    </Form>
                </Card>

                <Card className="mb-4">
                    <div className="flex justify-between items-center mb-4">
                        <Title level={4} className="mb-0">Actions</Title>
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: "load",
                                        icon: <UploadOutlined />,
                                        label: "Load from JSON"
                                    },
                                    {
                                        key: "upload",
                                        icon: <UploadOutlined />,
                                        label: "Upload JSON File"
                                    },
                                    {
                                        key: "export",
                                        icon: <ExportOutlined />,
                                        label: "Export Configuration"
                                    }
                                ],
                                onClick: ({ key }) => {
                                    if (key === 'load') {
                                        setIsLoadModalOpen(true);
                                    } else if (key === 'upload') {
                                        fileInputRef.current?.click();
                                    } else if (key === 'export') {
                                        exportConfiguration();
                                    }
                                }
                            }}
                            trigger={['click']}
                        >
                            <Button icon={<MenuOutlined />}>Import/Export</Button>
                        </Dropdown>
                    </div>

                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                        accept=".json"
                    />

                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setIsAddNodeModalOpen(true)}
                            block
                        >
                            Add Node
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={showSaveConfirmation}
                            block
                        >
                            Save Configuration
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={exportConfiguration}
                            block
                        >
                            Download Configuration
                        </Button>
                    </Space>
                </Card>

                <Card 
                    title={
                        <div className="flex items-center gap-2">
                            <BranchesOutlined />
                            <span>Transitions Preview</span>
                        </div>
                    } 
                    className="mb-4"
                >
                    {currentTransitions.length > 0 ? (
                        <div className="space-y-2">
                            {currentTransitions.map((transition, index) => (
                                <div 
                                    key={index} 
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                                >
                                    <div className="flex items-center gap-3">
                                        <Tag color="blue" className="min-w-0">
                                            {transition.from}
                                        </Tag>
                                        <ArrowRightOutlined className="text-gray-400" />
                                        <Tag color="green" className="min-w-0">
                                            {transition.to}
                                        </Tag>
                                    </div>
                                    <Tag color="orange" className="text-xs">
                                        {transition.event}
                                    </Tag>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Empty 
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No transitions generated yet"
                            className="py-4"
                        />
                    )}
                </Card>

                <Card title="Node Order" className="mb-4">
                    <List
                        dataSource={nodes}
                        renderItem={(node, index) => (
                            <List.Item
                                actions={[
                                    <Button
                                        icon={<ArrowUpOutlined />}
                                        onClick={() => moveNode(index, 'up')}
                                        disabled={index === 0}
                                        size="small"
                                    />,
                                    <Button
                                        icon={<ArrowDownOutlined />}
                                        onClick={() => moveNode(index, 'down')}
                                        disabled={index === nodes.length - 1}
                                        size="small"
                                    />,
                                    <Button
                                        icon={<DeleteOutlined />}
                                        // @ts-ignore
                                        onClick={() => removeNode(node.id)}
                                        danger
                                        size="small"
                                    />,
                                ]}
                            >
                                <div className="flex items-center space-x-2">
                                    <Text
                                        ellipsis
                                        // @ts-ignore
                                        className={selectedNode?.id === node.id ? 'font-bold text-blue-600' : ''}
                                        // @ts-ignore
                                        onClick={() => setSelectedNode(node)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {// @ts-ignore
                                            node.data.label
                                        }
                                    </Text>
                                    {
                                        // @ts-ignore
                                        node.data.level !== undefined && (
                                        <Text type="secondary" className="text-xs">
                                            (Level: {
                                            // @ts-ignore
                                            node.data.level})
                                        </Text>
                                    )}
                                </div>
                            </List.Item>
                        )}
                    />
                </Card>
            </Sider>

            {/* Center area - Flow diagram */}
            <Content className="relative">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChangeHandler}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onNodeClick={onNodeClick}
                    nodeTypes={nodeTypes}
                    fitView
                    attributionPosition="bottom-right"
                >
                    <Controls />
                    <MiniMap />
                    <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
                </ReactFlow>
            </Content>

            {/* Node Editor */}
            {selectedNode && (
                <NodeEditor
                    key={selectedNode.id}
                    node={selectedNode}
                    updateNode={updateNode}
                    // @ts-ignore
                    nodes={nodes}
                    maxLevel={maxLevel}
                    visible={!!selectedNode}
                    onClose={() => setSelectedNode(null)}
                />
            )}

            {/* Add Node Modal */}
            <Modal
                title="Add New Node"
                open={isAddNodeModalOpen}
                onOk={() => addNode(newNodeType)}
                onCancel={() => setIsAddNodeModalOpen(false)}
                okButtonProps={{ disabled: !newNodeType }}
            >
                <Form layout="vertical">
                    <Form.Item
                        label="Node Type"
                        required
                        help="Select the type of node to add to the workflow"
                    >
                        <div className="grid grid-cols-2 gap-2">
                            {nodeTypeOptions.map(option => (
                                <Button
                                    key={option.key}
                                    type={newNodeType === option.key ? 'primary' : 'default'}
                                    onClick={() => setNewNodeType(option.key)}
                                    className="text-left h-auto py-2"
                                    block
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Load Configuration Modal */}
            <Modal
                title="Load Workflow Configuration"
                open={isLoadModalOpen}
                onOk={handlePasteConfig}
                onCancel={() => {
                    setIsLoadModalOpen(false);
                    setPastedConfig('');
                }}
                okText="Load"
                okButtonProps={{ disabled: !pastedConfig }}
            >
                <Form layout="vertical">
                    <Form.Item
                        label="Configuration JSON"
                        required
                        help="Paste your workflow configuration JSON here"
                    >
                        <TextArea
                            rows={10}
                            value={pastedConfig}
                            onChange={(e) => setPastedConfig(e.target.value)}
                            placeholder="Paste your workflow configuration JSON here..."
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default WorkflowEditor;